import { useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { DEFAULT_ROUTINES, ROUTINE_SEMS, ROUTINE_DAYS } from "../../data/site";

const SLOTS_DEFAULT = ["Period 1", "Period 2", "Period 3", "Period 4", "Period 5", "Period 6", "Period 7"];
const NP = 7;

function normalize(routines) {
  const base = {};
  ROUTINE_SEMS.forEach((s) => {
    const r = (routines && routines[s]) || {};
    const srcSlots = (r.slots && r.slots.length ? r.slots : SLOTS_DEFAULT);
    const slots = Array.from({ length: NP }).map((_, i) => srcSlots[i] || SLOTS_DEFAULT[i]);
    const times = Array.from({ length: NP }).map((_, i) => (r.times && r.times[i]) || "");
    const days = {};
    ROUTINE_DAYS.forEach((d) => {
      const row = (r.days && r.days[d]) || [];
      days[d] = Array.from({ length: NP }).map((_, i) => {
        const c = row[i];
        if (c && typeof c === "object") return { subject: c.subject || "", room: c.room || "", teacher: c.teacher || "" };
        return { subject: typeof c === "string" ? c : "", room: "", teacher: "" };
      });
    });
    base[s] = { slots, times, days };
  });
  return base;
}

const cellInput = "w-full bg-transparent focus:bg-gold/10 outline-none px-2 py-1 rounded-sm text-center text-sm";

export default function RoutineEditor() {
  const { settings, reload } = useSite();
  const [routines, setRoutines] = useState(() => normalize(settings.routines || DEFAULT_ROUTINES));
  const [sem, setSem] = useState("Semester I");
  const [saving, setSaving] = useState(false);

  const cur = routines[sem];

  const setSlot = (i, v) => setRoutines((r) => { const c = structuredClone(r); c[sem].slots[i] = v; return c; });
  const setTime = (i, v) => setRoutines((r) => { const c = structuredClone(r); c[sem].times[i] = v; return c; });
  const setCell = (day, ci, field, v) => setRoutines((r) => { const c = structuredClone(r); c[sem].days[day][ci][field] = v; return c; });

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings({ ...settings, routines });
      toast.success(`${sem} routine saved`);
      await reload();
    } catch { toast.error("Failed to save routine"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="routine-editor">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-forest">Class Routine</h2>
          <p className="text-muted text-sm mt-1">Pick a semester, then fill Subject / Room / Teacher for each period. Leave blank if free.</p>
        </div>
        <button data-testid="save-routine-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-gold text-forest px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Routine
        </button>
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {ROUTINE_SEMS.map((s) => (
          <button key={s} data-testid="routine-editor-sem-tab" onClick={() => setSem(s)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${sem === s ? "bg-forest text-bone border-forest" : "border-line text-muted hover:border-forest"}`}>
            {s.replace("Semester ", "Sem ")}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto border border-line bg-white">
        <table className="w-full min-w-[980px] border-collapse text-sm">
          <thead>
            <tr>
              <th className="bg-forest text-bone text-left text-xs uppercase tracking-widest px-3 py-3 border border-line w-28">Day / Period</th>
              {cur.slots.map((s, i) => (
                <th key={i} className="bg-bone border border-line px-2 py-2">
                  <input data-testid={`routine-slot-${i}`} value={s} onChange={(e) => setSlot(i, e.target.value)} className="w-full text-xs uppercase tracking-widest text-forest bg-transparent outline-none text-center font-semibold" />
                </th>
              ))}
            </tr>
            <tr>
              <th className="bg-forest/90 text-bone text-left text-[10px] uppercase tracking-widest px-3 py-2 border border-line">Timing</th>
              {cur.times.map((t, i) => (
                <th key={i} className="bg-bone/60 border border-line px-2 py-1">
                  <input data-testid={`routine-time-${i}`} value={t} onChange={(e) => setTime(i, e.target.value)} placeholder="e.g. 9:00–9:45" className="w-full text-[11px] text-muted bg-transparent outline-none text-center" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROUTINE_DAYS.map((day) => (
              <tr key={day}>
                <td className="bg-bone font-serif text-base text-forest px-3 py-2 border border-line align-top">{day}</td>
                {cur.days[day].map((cell, ci) => (
                  <td key={ci} className="border border-line px-1 py-1 align-top min-w-[150px]">
                    <input data-testid={`routine-cell-${day}-${ci}`} value={cell.subject} onChange={(e) => setCell(day, ci, "subject", e.target.value)} placeholder="Subject" className={`${cellInput} font-medium`} />
                    <input data-testid={`routine-room-${day}-${ci}`} value={cell.room} onChange={(e) => setCell(day, ci, "room", e.target.value)} placeholder="Room No." className={`${cellInput} text-xs text-muted`} />
                    <input data-testid={`routine-teacher-${day}-${ci}`} value={cell.teacher} onChange={(e) => setCell(day, ci, "teacher", e.target.value)} placeholder="Teacher" className={`${cellInput} text-xs text-muted`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
